// Bucket maths shared by the desktop timeline chart, the mobile activity bars and
// any consumer that needs to turn a chart slot back into a [from, to) time window.
// Mirrors the server-side bucket picker in apps/api/src/routes/admin/metrics.ts so
// a rendered bar maps 1:1 to the rows the logs API returns for that window.

export type BucketSize = '5min' | '30min' | 'hour' | 'day';

const MIN_5_MS = 5 * 60 * 1000;
const MIN_30_MS = 30 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const TWO_HOURS_MS = 2 * HOUR_MS;
const TWELVE_HOURS_MS = 12 * HOUR_MS;
const SEVEN_DAYS_MS = 7 * DAY_MS;

// Day buckets are floored in the user's timezone (Madrid). Flooring to UTC produces an off-by-one
// at the month boundary: 2026-05-01T00:00 Madrid = 2026-04-30T22:00 UTC, which would floor to
// 2026-04-30 and create a phantom slot labeled "30" of the previous month.
const DAY_TZ = 'Europe/Madrid';

const dayPartsFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: DAY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const timeFmt = new Intl.DateTimeFormat('es-ES', {
  timeZone: DAY_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const dayFmt = new Intl.DateTimeFormat('es-ES', {
  timeZone: DAY_TZ,
  day: '2-digit',
  month: '2-digit',
});

export function pickBucketSize(fromMs: number, toMs: number): BucketSize {
  const diff = toMs - fromMs;
  if (diff >= SEVEN_DAYS_MS) return 'day';
  if (diff >= TWELVE_HOURS_MS) return 'hour';
  if (diff >= TWO_HOURS_MS) return '30min';
  return '5min';
}

export function bucketStepMs(size: BucketSize): number {
  if (size === '5min') return MIN_5_MS;
  if (size === '30min') return MIN_30_MS;
  if (size === 'hour') return HOUR_MS;
  return DAY_MS;
}

export function floorToBucket(d: Date, size: BucketSize): Date {
  if (size === '5min' || size === '30min' || size === 'hour') {
    const step = bucketStepMs(size);
    return new Date(Math.floor(d.getTime() / step) * step);
  }
  const parts = dayPartsFmt.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return new Date(`${get('year')}-${get('month')}-${get('day')}T00:00:00`);
}

export function generateBucketSlots(fromMs: number, toMs: number, size: BucketSize): number[] {
  const stepMs = bucketStepMs(size);
  const start = floorToBucket(new Date(fromMs), size).getTime();
  const end = floorToBucket(new Date(toMs), size).getTime();
  const slots: number[] = [];
  for (let t = start; t <= end; t += stepMs) {
    slots.push(t);
  }
  return slots;
}

// Human label for a selected slot, e.g. "20:00 – 21:00" or "14/08" for day buckets.
export function formatBucketRange(fromMs: number, size: BucketSize): string {
  const to = fromMs + bucketStepMs(size);
  if (size === 'day') return dayFmt.format(new Date(fromMs));
  return `${timeFmt.format(new Date(fromMs))} – ${timeFmt.format(new Date(to))}`;
}

// The exclusive end of a slot, expressed as the inclusive `to` the logs API expects
// (1 ms before the next slot starts) so adjacent buckets never double-count a log.
export function bucketEndIso(fromMs: number, size: BucketSize): string {
  return new Date(fromMs + bucketStepMs(size) - 1).toISOString();
}
